// OPS-1 Planner (2026-05-29) — date-oriented work planning. A TASK calendar,
// not an appointment calendar: tasks are organized by DUE DATE, no time-of-day.
// Views: Month (task counts + hours per day) · Week (daily workload) · Day
// (High Priority / Normal / Completed). NOT Outlook, no scheduling.
import { useState, useEffect, useCallback, useMemo } from "react";
import { apiFetch, fmt } from "../api";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => addDays(d, -d.getDay());
const sameDay = (a, b) => a && b && iso(a) === iso(b);
const PRI_COLOR = { Critical: "#dc2626", High: "#d97706", Medium: "#2563eb", Low: "#6b7280" };
const PRI_RANK = { Critical: 0, High: 1, Medium: 2, Low: 3 };

// BUG-4: how many task rows a Month-View day cell shows before "+N more".
// Scales with window width so a wider screen fits more, per Kuhrt's ask.
function useMonthCap() {
  const calc = () => {
    const w = typeof window !== "undefined" ? window.innerWidth : 1280;
    if (w >= 1900) return 7;
    if (w >= 1550) return 6;
    if (w >= 1250) return 5;
    if (w >= 1000) return 4;
    return 3;
  };
  const [cap, setCap] = useState(calc);
  useEffect(() => {
    const on = () => setCap(calc());
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return cap;
}

// Most-actionable first within a day: open before done, then by priority.
function sortDayTasks(tasks) {
  return [...tasks].sort((a, b) => {
    const ad = a.status === "Done" ? 1 : 0, bd = b.status === "Done" ? 1 : 0;
    if (ad !== bd) return ad - bd;
    return (PRI_RANK[a.priority] ?? 2) - (PRI_RANK[b.priority] ?? 2);
  });
}

export default function Planner({ onNavigateToClaim, openTask, workRefresh }) {
  const [view, setView]     = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [tasks, setTasks]   = useState([]);
  const [hoursMap, setHours] = useState({});

  // Visible range for the time rollup.
  const range = useMemo(() => {
    if (view === "day")  return [cursor, cursor];
    if (view === "week") { const s = startOfWeek(cursor); return [s, addDays(s, 6)]; }
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const gridStart = startOfWeek(first);
    return [gridStart, addDays(gridStart, 41)];
  }, [view, cursor]);

  const load = useCallback(() => {
    apiFetch("/me/tasks").then(setTasks).catch(() => {});
    apiFetch(`/me/time-by-day?date_from=${iso(range[0])}&date_to=${iso(range[1])}`)
      .then(rows => { const m = {}; (rows || []).forEach(r => { m[r.work_date] = Number(r.hours); }); setHours(m); })
      .catch(() => {});
  }, [range]);
  useEffect(() => { load(); }, [load, workRefresh]);

  const deleteTask = async (id) => {
    if (!window.confirm("Delete this task? This cannot be undone.")) return;
    try { await apiFetch(`/tasks/${id}`, { method: "DELETE" }); load(); }
    catch (e) { alert(e.message || "Delete failed."); }
  };

  // Bucket tasks by due_date ISO.
  const byDay = useMemo(() => {
    const m = {};
    tasks.forEach(t => { if (t.due_date) (m[t.due_date] = m[t.due_date] || []).push(t); });
    return m;
  }, [tasks]);

  const title = view === "month"
    ? cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : view === "week"
      ? `Week of ${startOfWeek(cursor).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
      : cursor.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  const step = (dir) => {
    if (view === "month") setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + dir, 1));
    else if (view === "week") setCursor(addDays(cursor, dir * 7));
    else setCursor(addDays(cursor, dir));
  };

  return (
    <div className="page">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button className="btn btn-outline" onClick={() => step(-1)}>‹</button>
          <div style={{ fontSize: 18, fontWeight: 700, color: "var(--navy)", minWidth: 200 }}>{title}</div>
          <button className="btn btn-outline" onClick={() => step(1)}>›</button>
          <button className="btn btn-outline" onClick={() => setCursor(new Date())}>Today</button>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ display: "inline-flex", border: "1px solid var(--border)", borderRadius: 4, overflow: "hidden" }}>
            {["month", "week", "day"].map(v => (
              <button key={v} onClick={() => setView(v)}
                      style={{ padding: "6px 14px", border: "none", fontSize: 12, fontWeight: 600, textTransform: "capitalize", cursor: "pointer", background: view === v ? "var(--navy)" : "#fff", color: view === v ? "#fff" : "var(--text2)" }}>{v}</button>
            ))}
          </span>
          <button className="btn btn-blue" onClick={() => openTask && openTask({})}>+ Task</button>
        </div>
      </div>

      {view === "month" && <MonthView cursor={cursor} range={range} byDay={byDay} hoursMap={hoursMap} onDay={(d) => { setCursor(d); setView("day"); }} />}
      {view === "week"  && <WeekView  cursor={cursor} byDay={byDay} hoursMap={hoursMap} onNavigateToClaim={onNavigateToClaim} openTask={openTask} onDelete={deleteTask} />}
      {view === "day"   && <DayView   cursor={cursor} byDay={byDay} hoursMap={hoursMap} onNavigateToClaim={onNavigateToClaim} openTask={openTask} onDelete={deleteTask} />}
    </div>
  );
}

function MonthView({ cursor, range, byDay, hoursMap, onDay }) {
  const days = []; for (let i = 0; i < 42; i++) days.push(addDays(range[0], i));
  const today = new Date();
  const cap = useMonthCap();
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 1, marginBottom: 1 }}>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
          <div key={d} style={{ textAlign: "center", fontSize: 11, fontWeight: 700, color: "var(--text3)", padding: "6px 0" }}>{d}</div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 6 }}>
        {days.map((d, i) => {
          const inMonth = d.getMonth() === cursor.getMonth();
          const dayTasks = sortDayTasks(byDay[iso(d)] || []);
          const hrs = hoursMap[iso(d)] || 0;
          const overdue = dayTasks.some(t => t.status !== "Done") && d < new Date(today.toDateString());
          const shown = dayTasks.slice(0, cap);
          const extra = dayTasks.length - shown.length;
          return (
            <div key={i} onClick={() => onDay(d)}
                 style={{ minHeight: 88, border: "1px solid var(--border)", borderRadius: 6, padding: "6px 8px", cursor: "pointer", background: inMonth ? "#fff" : "#f7f8fa", opacity: inMonth ? 1 : 0.55, position: "relative" }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 4 }}>
                <span style={{ fontSize: 12, fontWeight: sameDay(d, today) ? 800 : 500, color: sameDay(d, today) ? "var(--blue)" : "var(--text2)" }}>{d.getDate()}</span>
                {dayTasks.length > 0 && (
                  <span style={{ fontSize: 10, fontWeight: 700, color: overdue ? "#dc2626" : "var(--text3)" }}>{dayTasks.length}</span>
                )}
              </div>
              {shown.length > 0 && (
                <div style={{ marginTop: 3, display: "flex", flexDirection: "column", gap: 2 }}>
                  {shown.map(t => {
                    const done = t.status === "Done";
                    return (
                      <div key={t.id} title={`${t.title}${t.claim_number ? ` · ${t.claim_number}` : " · Personal"}${t.priority ? ` · ${t.priority}` : ""}`}
                           style={{ display: "flex", alignItems: "center", gap: 4, overflow: "hidden" }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", flexShrink: 0, background: PRI_COLOR[t.priority] || "#6b7280", opacity: done ? 0.4 : 1 }} />
                        <span style={{ fontSize: 10.5, lineHeight: 1.35, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                       color: done ? "var(--text3)" : "var(--text2)", textDecoration: done ? "line-through" : "none" }}>{t.title}</span>
                      </div>
                    );
                  })}
                  {extra > 0 && <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 1 }}>+{extra} more</div>}
                </div>
              )}
              {hrs > 0 && <div style={{ fontSize: 10.5, color: "var(--text3)", marginTop: 3 }}>{hrs.toFixed(1)} hrs</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekView({ cursor, byDay, hoursMap, onNavigateToClaim, openTask, onDelete }) {
  const s = startOfWeek(cursor);
  const days = []; for (let i = 0; i < 7; i++) days.push(addDays(s, i));
  const today = new Date();
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 8 }}>
      {days.map((d, i) => {
        const dayTasks = byDay[iso(d)] || [];
        const hrs = hoursMap[iso(d)] || 0;
        return (
          <div key={i} style={{ border: "1px solid var(--border)", borderRadius: 8, minHeight: 320, background: sameDay(d, today) ? "#f5f8ff" : "#fff" }}>
            <div style={{ padding: "8px 10px", borderBottom: "1px solid var(--border)", textAlign: "center" }}>
              <div style={{ fontSize: 11, color: "var(--text3)", fontWeight: 600 }}>{d.toLocaleDateString("en-US", { weekday: "short" })}</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: sameDay(d, today) ? "var(--blue)" : "var(--navy)" }}>{d.getDate()}</div>
              <div style={{ fontSize: 10, color: "var(--text3)" }}>{dayTasks.length} task{dayTasks.length === 1 ? "" : "s"}{hrs > 0 ? ` · ${hrs.toFixed(1)}h` : ""}</div>
            </div>
            <div style={{ padding: 6, display: "flex", flexDirection: "column", gap: 4 }}>
              {dayTasks.map(t => <TaskPill key={t.id} t={t} onNavigateToClaim={onNavigateToClaim} openTask={openTask} onDelete={onDelete} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DayView({ cursor, byDay, hoursMap, onNavigateToClaim, openTask, onDelete }) {
  const dayTasks = byDay[iso(cursor)] || [];
  const high = dayTasks.filter(t => t.status !== "Done" && ["High", "Critical"].includes(t.priority));
  const normal = dayTasks.filter(t => t.status !== "Done" && !["High", "Critical"].includes(t.priority));
  const done = dayTasks.filter(t => t.status === "Done");
  const hrs = hoursMap[iso(cursor)] || 0;
  const Section = ({ label, items, color }) => (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-header"><h3 style={{ color }}>{label}</h3><span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text3)" }}>{items.length}</span></div>
      {items.length === 0 ? <div className="empty-state">None</div>
        : <div style={{ padding: 8, display: "flex", flexDirection: "column", gap: 6 }}>{items.map(t => <TaskPill key={t.id} t={t} big onNavigateToClaim={onNavigateToClaim} openTask={openTask} onDelete={onDelete} />)}</div>}
    </div>
  );
  return (
    <div>
      {hrs > 0 && <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 12 }}>🕑 {hrs.toFixed(1)} hours logged this day</div>}
      <Section label="High Priority" items={high} color="#dc2626" />
      <Section label="Normal" items={normal} color="var(--navy)" />
      <Section label="Completed" items={done} color="var(--green)" />
    </div>
  );
}

function TaskPill({ t, big, onNavigateToClaim, openTask, onDelete }) {
  const done = t.status === "Done";
  const btn = { background: "transparent", border: "none", cursor: "pointer", fontSize: 12, color: "var(--text3)", padding: "0 2px", lineHeight: 1 };
  return (
    <div onClick={() => t.claim_id ? (onNavigateToClaim && onNavigateToClaim(t.claim_id)) : (openTask && openTask({ task: t }))}
         style={{ display: "flex", gap: 6, alignItems: "center", padding: big ? "8px 10px" : "5px 7px", borderRadius: 5, background: "#fff", border: "1px solid var(--border)", borderLeft: `3px solid ${PRI_COLOR[t.priority] || "#6b7280"}`, cursor: "pointer", opacity: done ? 0.6 : 1 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: big ? 13 : 11.5, fontWeight: 500, color: "var(--navy)", textDecoration: done ? "line-through" : "none", whiteSpace: big ? "normal" : "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.title}</div>
        <div className="mono" style={{ fontSize: 10, color: t.claim_number ? "var(--blue)" : "var(--text3)" }}>{t.claim_number || "Personal"}{t.assigned_to_name ? ` · ${t.assigned_to_name}` : ""}</div>
      </div>
      {openTask && <button title="Edit" onClick={(e) => { e.stopPropagation(); openTask({ task: t }); }} style={btn}>✎</button>}
      {onDelete && <button title="Delete" onClick={(e) => { e.stopPropagation(); onDelete(t.id); }} style={{ ...btn, color: "#dc2626" }}>🗑</button>}
    </div>
  );
}
